import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Textarea } from "./ui/textarea";
import { MoreHorizontal, Eye, UserCog, UserX, RotateCcw, Compass } from "lucide-react";
import { accountApi } from "../lib/api";

const PROGRAM_CONTEXTS = ["onboarding", "renforcement", "lancement", "retard_chiffres", "entretien"];
const EXPERIENCE_LEVELS = ["debutant", "confirme", "experimente"];

interface ManagerCandidate {
  id: string;
  firstName: string;
  lastName: string;
  profileType: string;
}

interface CollaboratorRowActionsProps {
  collab: { id: string; firstName: string; lastName: string; managerId?: string | null; status?: string };
  candidates: ManagerCandidate[];
  accountId?: string;
  /** @deprecated Les libellés passent par i18n ; conservé pour les appelants existants. */
  isFr?: boolean;
  /** Œil « voir comme cet utilisateur » (impersonation ADM). */
  canImpersonate?: boolean;
  /** Menu « changer le manager » (BNF/MNG, géré par un user habilité). */
  canChangeManager?: boolean;
  /** Panneau « contexte actif & niveau d'expérience » (manager/ORG, vision engagement #3). */
  canManageProgram?: boolean;
  /** Désactiver / réactiver (droit « status » de `collaborator-permissions.ts`). */
  canToggleStatus?: boolean;
  /**
   * `row` (défaut) : icônes compactes, alignées d'une ligne à l'autre (menu « ⋯ » en étroit).
   * `toolbar` : vrais boutons libellés, pour la barre d'actions de la fiche collaborateur.
   */
  variant?: "row" | "toolbar";
  onImpersonate?: () => void;
  onManagerSaved?: (newManagerId: string | null) => void;
  onDeactivate?: () => void;
  onReactivate?: () => void;
}

/**
 * Actions secondaires d'une ligne collaborateur, en deux rendus responsive :
 * - écran large (≥ xl) : boutons affichés inline (comportement historique) ;
 * - écran étroit (< xl) : repli automatique dans un menu « ⋯ » pour ne jamais
 *   tronquer / masquer une action (cf. overflow horizontal de la table + vue carte mobile).
 *
 * Les actions et leurs conditions d'affichage sont identiques dans les deux rendus :
 * seule la présentation change. L'œil « voir comme » et le « désactiver/réactiver »
 * restent donc toujours accessibles quelle que soit la largeur de la fenêtre.
 */
export function CollaboratorRowActions({
  collab,
  candidates,
  accountId,
  canImpersonate = false,
  canChangeManager = false,
  canManageProgram = false,
  canToggleStatus = true,
  variant = "row",
  onImpersonate,
  onManagerSaved,
  onDeactivate,
  onReactivate,
}: CollaboratorRowActionsProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>(collab.managerId || "none");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Panneau « contexte actif & niveau »
  const [progOpen, setProgOpen] = useState(false);
  const [progLoading, setProgLoading] = useState(false);
  const [progSaving, setProgSaving] = useState(false);
  const [progError, setProgError] = useState<string | null>(null);
  const [ctx, setCtx] = useState<string>("none");
  const [ctxNote, setCtxNote] = useState<string>("");
  const [level, setLevel] = useState<string>("confirme");
  const [level0, setLevel0] = useState<string>("confirme");

  async function openProgramDialog() {
    setProgOpen(true);
    setProgError(null);
    setProgLoading(true);
    try {
      const s = await accountApi.getCollaboratorProgramState(collab.id, accountId);
      setCtx(s.context || "none");
      setCtxNote(s.note || "");
      setLevel(s.experienceLevel || "confirme");
      setLevel0(s.experienceLevel || "confirme");
    } catch (err: any) {
      setProgError(err?.message || t("common.loadError"));
    } finally {
      setProgLoading(false);
    }
  }

  async function handleSaveProgram() {
    setProgSaving(true);
    setProgError(null);
    try {
      await accountApi.setCollaboratorProgramState(collab.id, { context: ctx === "none" ? null : ctx, note: ctxNote }, accountId);
      if (level !== level0) {
        await accountApi.updateCollaborator(collab.id, { experienceLevel: level }, accountId);
      }
      setProgOpen(false);
    } catch (err: any) {
      setProgError(err?.message || t("api.updateError"));
    } finally {
      setProgSaving(false);
    }
  }

  const isDisabled = collab.status === "disabled";
  const eligible = candidates.filter(
    (c) => (c.profileType === "MNG" || c.profileType === "ORG") && c.id !== collab.id,
  );

  function openManagerDialog() {
    setSelectedId(collab.managerId || "none");
    setErrorMsg(null);
    setDialogOpen(true);
  }

  async function handleSaveManager() {
    setSaving(true);
    setErrorMsg(null);
    try {
      const newManagerId = selectedId === "none" ? null : selectedId;
      await accountApi.updateCollaborator(collab.id, { managerId: newManagerId }, accountId);
      onManagerSaved?.(newManagerId);
      setDialogOpen(false);
    } catch (err: any) {
      setErrorMsg(err?.message || t("api.updateError"));
    } finally {
      setSaving(false);
    }
  }

  const impersonateLabel = t("account.viewAsUserTest");
  const changeManagerLabel = t("account.changeManager");
  const placeholder = <span className="inline-block h-8 w-8" aria-hidden="true" />;
  const programLabel = t("programContext.action");
  const deactivateLabel = t("account.deactivate");
  const reactivateLabel = t("account.reactivate");

  // Barre d'actions de la fiche : de vrais boutons (contour, relief, couleur) et des mots,
  // la place ne manque pas. Couleurs de la charte (textes contrastés WCAG).
  const toolbarBtn = "h-9 gap-1.5 border-2 bg-white shadow-sm font-medium hover:shadow";
  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      {canImpersonate && (
        <Button variant="outline" size="sm" className={`${toolbarBtn} border-[#F6C847] text-[#926B00] hover:bg-[#F6C847]/15 hover:text-[#926B00]`} onClick={onImpersonate}>
          <Eye className="h-4 w-4" />
          {t("account.fiche.viewAs")}
        </Button>
      )}
      {canChangeManager && (
        <Button variant="outline" size="sm" className={`${toolbarBtn} border-slate-300 text-[#384872]`} onClick={openManagerDialog}>
          <UserCog className="h-4 w-4" />
          {changeManagerLabel}
        </Button>
      )}
      {canManageProgram && (
        <Button variant="outline" size="sm" className={`${toolbarBtn} border-[#988EF2] text-[#5A4BBE] hover:bg-[#988EF2]/15 hover:text-[#5A4BBE]`} onClick={openProgramDialog}>
          <Compass className="h-4 w-4" />
          {programLabel}
        </Button>
      )}
      {canToggleStatus && (isDisabled ? (
        <Button variant="outline" size="sm" className={`${toolbarBtn} border-[#7CBF94] text-[#1E7A3C] hover:bg-[#7CBF94]/15 hover:text-[#1E7A3C]`} onClick={onReactivate}>
          <RotateCcw className="h-4 w-4" />
          {reactivateLabel}
        </Button>
      ) : (
        <Button variant="outline" size="sm" className={`${toolbarBtn} border-[#F49390] text-[#C0423F] hover:bg-[#F49390]/15 hover:text-[#C0423F]`} onClick={onDeactivate}>
          <UserX className="h-4 w-4" />
          {deactivateLabel}
        </Button>
      ))}
    </div>
  );

  return (
    <>
      {variant === "toolbar" ? toolbar : (<>
      {/* Rendu large (≥ xl) : actions inline */}
      <div className="hidden xl:flex items-center gap-1">
        {/* Emplacements de largeur fixe (placeholder si l'action n'existe pas pour la ligne)
            → chaque action tombe dans sa propre colonne, alignée sur toutes les lignes. */}
        {canImpersonate ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-orange-500 hover:text-orange-600"
            aria-label={impersonateLabel}
            title={impersonateLabel}
            onClick={onImpersonate}
          >
            <Eye className="h-4 w-4" />
          </Button>
        ) : placeholder}
        {canChangeManager ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={changeManagerLabel}
            title={changeManagerLabel}
            onClick={openManagerDialog}
          >
            <UserCog className="h-4 w-4" />
          </Button>
        ) : placeholder}
        {canManageProgram ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            style={{ color: "#5A4BBE" }}
            aria-label={programLabel}
            title={programLabel}
            onClick={openProgramDialog}
          >
            <Compass className="h-4 w-4" />
          </Button>
        ) : placeholder}
        {canToggleStatus && (
        <div className="inline-flex w-[112px] justify-end">
          {isDisabled ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-green-600 hover:text-green-700"
              onClick={onReactivate}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              {reactivateLabel}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-destructive hover:text-destructive"
              onClick={onDeactivate}
            >
              <UserX className="h-3.5 w-3.5 mr-1" />
              {deactivateLabel}
            </Button>
          )}
        </div>
        )}
      </div>

      {/* Rendu étroit (< xl) : repli dans un menu « ⋯ » */}
      <div className="flex xl:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t("account.moreActions")}
              title={t("account.moreActions")}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canImpersonate && (
              <DropdownMenuItem onSelect={() => onImpersonate?.()}>
                <Eye className="h-4 w-4 mr-2 text-orange-500" />
                {impersonateLabel}
              </DropdownMenuItem>
            )}
            {canChangeManager && (
              <DropdownMenuItem onSelect={() => openManagerDialog()}>
                <UserCog className="h-4 w-4 mr-2" />
                {changeManagerLabel}
              </DropdownMenuItem>
            )}
            {canManageProgram && (
              <DropdownMenuItem onSelect={() => openProgramDialog()}>
                <Compass className="h-4 w-4 mr-2" style={{ color: "#5A4BBE" }} />
                {programLabel}
              </DropdownMenuItem>
            )}
            {canToggleStatus && (isDisabled ? (
              <DropdownMenuItem onSelect={() => onReactivate?.()}>
                <RotateCcw className="h-4 w-4 mr-2 text-green-600" />
                {reactivateLabel}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => onDeactivate?.()}
              >
                <UserX className="h-4 w-4 mr-2" />
                {deactivateLabel}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      </>)}

      {/* Dialog « changer le manager » partagé par les deux rendus */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) setDialogOpen(v); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{changeManagerLabel}</DialogTitle>
            <DialogDescription>
              {t("account.reassignManager", { name: `${collab.firstName} ${collab.lastName}` })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>{t("account.manager")}</Label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("account.noManager")}</SelectItem>
                {eligible.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSaveManager} disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog « contexte actif & niveau d'expérience » (vision engagement #3) */}
      <Dialog open={progOpen} onOpenChange={(v) => { if (!progSaving) setProgOpen(v); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("programContext.title")}</DialogTitle>
            <DialogDescription>
              {t("programContext.description", { name: `${collab.firstName} ${collab.lastName}` })}
            </DialogDescription>
          </DialogHeader>
          {progLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">{t("common.loading")}</div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>{t("programContext.contextLabel")}</Label>
                <Select value={ctx} onValueChange={setCtx}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("programContext.none")}</SelectItem>
                    {PROGRAM_CONTEXTS.map((c) => (
                      <SelectItem key={c} value={c}>{t(`programContext.contexts.${c}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {ctx !== "none" && (
                  <p className="text-xs text-muted-foreground">{t(`programContext.hints.${ctx}`)}</p>
                )}
              </div>
              {ctx !== "none" && (
                <div className="space-y-1.5">
                  <Label>{t("programContext.noteLabel")}</Label>
                  <Textarea value={ctxNote} onChange={(e) => setCtxNote(e.target.value)} rows={2} placeholder={t("programContext.notePlaceholder")} />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>{t("programContext.levelLabel")}</Label>
                <Select value={level} onValueChange={setLevel}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_LEVELS.map((l) => (
                      <SelectItem key={l} value={l}>{t(`programContext.levels.${l}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t("programContext.levelHint")}</p>
              </div>
              {progError && <p className="text-sm text-destructive">{progError}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgOpen(false)} disabled={progSaving}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSaveProgram} disabled={progSaving || progLoading}>
              {progSaving ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
